import { type ComponentType, type ReactNode, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { PageHeader } from './PageHeader';

/** A lucide-style icon: any component that takes an optional `className`. */
type SectionIcon = ComponentType<{ className?: string }>;

/**
 * A section within a {@link SectionLayout}: a nav destination plus its label.
 * `to` is a full route path (e.g. '/admin/users'); the matching route renders
 * into the layout's `<Outlet/>`.
 */
export interface SectionNavItem {
  to: string;
  label: string;
  /** Passed through to NavLink; defaults to exact-match (`true`). */
  end?: boolean;
  /** Optional trailing adornment (a count, a status dot). */
  badge?: ReactNode;
  /** Icon shown in the compact mobile bar (below `md`). */
  icon?: SectionIcon;
}

/** A labeled cluster of sections. Omit `label` for a flat, ungrouped list. */
export interface SectionNavGroup {
  label?: string;
  items: SectionNavItem[];
}

/** How many slots the mobile bar shows before collapsing the tail into "More". */
const MOBILE_MAX_SLOTS = 5;

/**
 * The reusable shell for any page split into sibling sections (Admin, Settings,
 * …). Renders the page header, a section navigator, and the active section's
 * content via `<Outlet/>`.
 *
 * The navigator is responsive with grouping preserved at every size:
 * - **md and up:** a sticky left rail with group headers; content fills the rest.
 * - **below md:** the rail collapses to a horizontal icon bar; sections past the
 *   fifth slot fold into a trailing "More" (`…`) menu.
 */
export function SectionLayout({
  title,
  subtitle,
  ariaLabel,
  groups,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Accessible name for the nav (rail + mobile bar). */
  ariaLabel: string;
  groups: SectionNavGroup[];
  actions?: ReactNode;
}) {
  const items = groups.flatMap((g) => g.items);

  return (
    <div className="space-y-6">
      <PageHeader title={title} subtitle={subtitle} actions={actions} />

      <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:gap-8 lg:grid-cols-[13rem_minmax(0,1fr)]">
        {/* One nav wraps both presentations; only one is visible per breakpoint. */}
        <nav aria-label={ariaLabel} className="mb-6 md:mb-0">
          {/* Below md: compact icon bar with a trailing overflow menu. */}
          <div className="md:hidden">
            <MobileSectionBar items={items} />
          </div>

          {/* md+: sticky grouped rail. */}
          <div className="sticky top-20 hidden space-y-5 md:block">
            {groups.map((group, i) => (
              <div key={group.label ?? `group-${i}`} className="space-y-1">
                {group.label && (
                  <div className="px-2 font-display text-2xs font-semibold uppercase tracking-[0.08em] text-ink-faint">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => (
                  <RailLink key={item.to} item={item} />
                ))}
              </div>
            ))}
          </div>
        </nav>

        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function RailLink({ item }: { item: SectionNavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.end ?? true}
      className={({ isActive }) =>
        `flex items-center justify-between gap-2 rounded-[5px] px-2.5 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 ${
          isActive
            ? 'bg-surface-2 font-medium text-ink'
            : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
        }`
      }
    >
      <span className="truncate">{item.label}</span>
      {item.badge != null && <span className="flex-none">{item.badge}</span>}
    </NavLink>
  );
}

// ── Mobile icon bar ──────────────────────────────────────────────────────────

function MobileSectionBar({ items }: { items: SectionNavItem[] }) {
  const { pathname } = useLocation();
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const overflow = items.length > MOBILE_MAX_SLOTS;
  const primary = overflow ? items.slice(0, MOBILE_MAX_SLOTS - 1) : items;
  const extra = overflow ? items.slice(MOBILE_MAX_SLOTS - 1) : [];
  const activeInExtra = extra.some((it) => isActive(it.to));
  const columns = primary.length + (overflow ? 1 : 0);

  return (
    <div
      className="grid gap-1 rounded-[8px] border border-line bg-surface-1 p-1"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {primary.map((item) => (
        <BarButton key={item.to} item={item} active={isActive(item.to)} />
      ))}
      {overflow && <MoreMenu items={extra} active={activeInExtra} isActive={isActive} />}
    </div>
  );
}

/** Shared visual for a bar cell: icon over a tiny truncated label. */
const barCellClass = (active: boolean) =>
  `flex w-full flex-col items-center gap-1 rounded-[5px] px-1 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 ${
    active ? 'bg-surface-2 text-signal' : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
  }`;

function BarButton({ item, active }: { item: SectionNavItem; active: boolean }) {
  const navigate = useNavigate();
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={() => navigate(item.to)}
      aria-current={active ? 'page' : undefined}
      className={barCellClass(active)}
    >
      {Icon && <Icon className="h-5 w-5 flex-none" aria-hidden="true" />}
      <span className="w-full truncate text-center font-display text-[10px] font-medium uppercase tracking-[0.04em] leading-none">
        {item.label}
      </span>
    </button>
  );
}

function MoreMenu({
  items,
  active,
  isActive,
}: {
  items: SectionNavItem[];
  active: boolean;
  isActive: (to: string) => boolean;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on route change so the menu never lingers over the new section.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More sections"
        onClick={() => setOpen((o) => !o)}
        className={barCellClass(active && !open)}
      >
        <MoreHorizontal className="h-5 w-5 flex-none" aria-hidden="true" />
        <span className="font-display text-[10px] font-medium uppercase tracking-[0.04em] leading-none">
          More
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-44 animate-fade-in rounded-[8px] border border-line-strong bg-surface-1 p-1 shadow-lg"
        >
          {items.map((item) => {
            const Icon = item.icon;
            const itemActive = isActive(item.to);
            return (
              <button
                key={item.to}
                type="button"
                role="menuitem"
                aria-current={itemActive ? 'page' : undefined}
                onClick={() => navigate(item.to)}
                className={`flex w-full items-center gap-2.5 rounded-[5px] px-2.5 py-2 text-sm transition-colors ${
                  itemActive
                    ? 'bg-surface-2 font-medium text-ink'
                    : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                }`}
              >
                {Icon && <Icon className="h-4 w-4 flex-none" aria-hidden="true" />}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
